import React, { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledForm, EnterIcon, FormFieldProps, Input, IOption, NavigationButton, Preloader, SidebarButtons, Spacing, useControlledForm, useToast } from '@netfront/ui-library';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { UserGroupContactFormProps } from '..';
import { CountryCodeInput, getFormFieldErrorMessage } from '../../../../../components';
import { useDeleteGroupContact, useGetCountryCodes, useUpdateGroupContact } from '../../../../../hooks';

const UserGroupContactForm = ({ contact, onOpenUpdateContacts, onUpdateContacts }: UserGroupContactFormProps) => {
  const { handleToastError, handleToastSuccess } = useToast();
  const [defaultValues, setDefaultValues] = useState<FormFieldProps>();
  const [countryCodeOptions, setCountryCodeOptions] = useState<IOption[]>([]);

  const { control, handleSubmit, reset } = useControlledForm({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        firstname: yup.string().label('First name').required(),
        lastname: yup.string().label('Last name').required(),
        email: yup.string().label('Email').email('Please enter a valid email').required(),
        phoneNumber: yup.string().label('Phone number'),
        communityName: yup.string().label('Display name'),
      }),
    ),
  });

  const { groupId } = contact;

  const { handleGetCountryCodes, isLoading: isGetCountryCodesLoading = false } = useGetCountryCodes({
    onCompleted: ({ enumValues = [] }) => {
      setCountryCodeOptions(enumValues.map(({ name, description }) => (
        {
          id: name,
          name: description,
          value: name,
        }
      )));
    },
    onError: (error) => {
      handleToastError({
        error,
      });
    },
  });

  const { handleUpdateGroupContact, isLoading: isUpdateContactLoading = false } = useUpdateGroupContact({
    onCompleted: () => {
      reset();
      onOpenUpdateContacts();
      onUpdateContacts();
      handleToastSuccess({
        message: 'Contact updated successfully!',
      });
    } ,
    onError: (error) => { 
      handleToastError({
        error,
      });
    },
  });
  
  const { handleDeleteGroupContact, isLoading: isDeleteContactLoading = false } = useDeleteGroupContact({
    onCompleted: () => {
      reset();
      onOpenUpdateContacts();
      onUpdateContacts();
      handleToastSuccess({
        message: 'Contact deleted successfully!',
      });
    } ,
    onError: (error) => { 
      handleToastError({
        error,
      });
    },
  });

  const handleDeleteContact = () => {
    void handleDeleteGroupContact({
      groupId,
      userId: contact.id,
    });
  };

  const handleSaveContact = (item: FormFieldProps) => {
    const {
      id: userId,
      firstname,
      lastname,
      email,
      phoneNumber,
      communityName,
      phoneNumberCountryCode
    } = item;

    void handleUpdateGroupContact({
      userId,
      firstname,
      lastname,
      email,
      phoneNumber,
      communityName,
      phoneNumberCountryCode: phoneNumberCountryCode ? phoneNumberCountryCode : undefined,
    });
  };

  useEffect(() => {
    handleGetCountryCodes();
    setDefaultValues(contact);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  const isLoading = isUpdateContactLoading || isDeleteContactLoading || isGetCountryCodesLoading;

  return (
    <>
      <Preloader isLoading={isLoading} />

      <Spacing size="x-large">
        <NavigationButton
          additionalClassNames="c-sidebar-navigation-button"
          direction="previous"
          icon={EnterIcon}
          rotationCssSuffix="180"
          text="Back to contacts"
          onClick={onOpenUpdateContacts}
        />
      </Spacing>

      <ControlledForm
        callBack={(item: FormFieldProps) => {
          handleSaveContact(item);
        }}
        handleSubmit={handleSubmit}
        isStopPropagation
      >
        <Controller 
          control={control}
          name="firstname"
          render={({ field, fieldState }) => (
            <Input
              errorMessage={getFormFieldErrorMessage(fieldState)}
              id="id_first_name"
              labelText="First name"
              tooltipPosition="start"
              tooltipText="Contact first name"
              type="text"
              isLabelSideBySide
              isRequired
              {...field}
            />
          )}
        />

        <Controller 
          control={control}
          name="lastname"
          render={({ field, fieldState }) => (
            <Input
              errorMessage={getFormFieldErrorMessage(fieldState)}
              id="id_last_name"
              labelText="Last name"
              tooltipPosition="start"
              tooltipText="Contact last name"
              type="text"
              isLabelSideBySide
              isRequired
              {...field}
            />
          )}
        />
        
        <Controller 
          control={control}
          name="communityName"
          render={({ field }) => (
            <Input
              id="id_community_name"
              labelText="Display name"
              tooltipPosition="start"
              tooltipText="Displayed contact name"
              type="text"
              isLabelSideBySide
              {...field}
            />
          )}
        />
        
        <Controller 
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              id="id_email"
              labelText="Email"
              tooltipPosition="start"
              tooltipText="Contact email"
              type="text"
              isLabelSideBySide
              {...field}
            />
          )}
        />
        <CountryCodeInput 
          codesOptions={countryCodeOptions} 
          control={control}
          countryCodeName="phoneNumberCountryCode"
          id="id_group_phone_number"
          phoneNumberName='phoneNumber'
        />

        <SidebarButtons
          buttonSize="xs"
          onCancel={onOpenUpdateContacts}
          onDelete={handleDeleteContact}
          onSaveButtonType="submit"
        />
      </ControlledForm>
    </>
  );
};

export { UserGroupContactForm };
